/**
 * Самостоятельная регистрация клиента-физлица (auth-and-roles). Роль назначает бэкенд. На
 * внутреннем адресе кабинета сотрудников (team-zone-login-page) регистрации нет — ссылка на
 * клиентский кабинет.
 */
import { useState, type FormEvent } from 'react'
import { useAuth } from '@/app/auth-context'
import { useEntryZone } from '@/app/entry-zone'
import { Link } from '@/design-system/components/primitives/link'
import { AppLink } from '@/app/navigation'
import { AuthShell } from './auth-shell'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { EducationSelect } from '@/company/education-select'
import { PasswordInput } from '@/design-system/components/primitives/password-input'
import { DateInput } from '@/design-system/components/primitives/date-input'
import { SnilsInput } from '@/design-system/components/primitives/snils-input'
import { SNILS_MESSAGE } from '@/lib/snils'
import { isValidSnils } from '@/lib/validation'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import { getErrorMessage } from '@/lib/errors'
import type { RegisterIndividualInput } from '@/lib/api'
import { normalizePersonName } from '@/lib/name-case'

export function RegisterPage() {
  const { register } = useAuth()
  const zone = useEntryZone()
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [gender, setGender] = useState<'male' | 'female' | ''>('')
  const [birthDate, setBirthDate] = useState('')
  const [snils, setSnils] = useState('')
  const [education, setEducation] = useState('')
  // СНИЛС (centrika-docs!46): поле необязательное, но ЗАПОЛНЕННОЕ обязано быть корректным.
  const snilsError = snils.trim() !== '' && !isValidSnils(snils) ? SNILS_MESSAGE : undefined
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    const input: RegisterIndividualInput = {
      fullName,
      email,
      password,
      gender: gender || undefined,
      birthDate: birthDate || undefined,
      snils: snils || undefined,
      education: education || undefined,
    }
    try {
      await register(input)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось зарегистрироваться'))
      setSubmitting(false)
    }
  }

  if (zone?.zone === 'team') {
    return (
      <AuthShell footer={<AppLink to="/login">Вход для сотрудников</AppLink>}>
        <Alert variant="info">
          Это кабинет сотрудников Центрики — регистрации здесь нет.
          {zone.clientUrl ? (
            <>
              {' '}
              Зарегистрироваться можно в{' '}
              <Link href={`${zone.clientUrl.replace(/\/$/, '')}/register`}>
                личном кабинете клиента
              </Link>
              .
            </>
          ) : null}
        </Alert>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      subtitle="Регистрация физлица — доступ к обучению"
      footer={
        <span className="text-txt-secondary">
          Уже есть аккаунт? <AppLink to="/login">Войти</AppLink>
        </span>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        {error ? (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        ) : null}
        <Field required label="ФИО">
          {(props) => (
            <Input
              {...props}
              autoComplete="name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              // «Иванов Иван Иванович» — на уходе с поля (names-and-positions-normalization).
              onBlur={() => setFullName(normalizePersonName(fullName))}
              spellCheck={false}
              required
            />
          )}
        </Field>
        <Field required label="Электронная почта">
          {(props) => (
            <Input
              {...props}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          )}
        </Field>
        <Field required label="Пароль">
          {(props) => (
            <PasswordInput
              {...props}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          )}
        </Field>
        <Field label="Пол">
          {(props) => (
            <Select value={gender} onValueChange={(v) => setGender(v as 'male' | 'female')}>
              <SelectTrigger id={props.id} aria-describedby={props['aria-describedby']}>
                <SelectValue placeholder="Не указан" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Мужской</SelectItem>
                <SelectItem value="female">Женский</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Дата рождения">
          {(props) => (
            <DateInput {...props} value={birthDate} onChange={setBirthDate} />
          )}
        </Field>
        <Field label="СНИЛС" hint="Необязательно" error={snilsError}>
          {(props) => (
            <SnilsInput
              {...props}
              value={snils}
              invalid={Boolean(snilsError)}
              onChange={setSnils}
            />
          )}
        </Field>
        <Field label="Образование" hint="Необязательно">
          {(props) => <EducationSelect id={props.id} value={education} onChange={setEducation} />}
        </Field>
        <Button
          type="submit"
          className="mt-2 w-full justify-center"
          disabled={submitting || Boolean(snilsError)}
        >
          {submitting ? 'Регистрация…' : 'Зарегистрироваться'}
        </Button>
      </form>
    </AuthShell>
  )
}
